import { observer } from 'mobx-react';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContextProvider } from '../../../AppLoader';
import { EnforcementCard } from '../EnforcementCard';
import { CodeStepCard } from '../CodeStepCard';
import { ButtonWithLoader } from '../../../ButtonWithLoader/ButtonWithLoader';
import { ThemedText } from '../../../ThemedComponents';
import { UiStore } from './AddPhoneUiStore';
import { AddPhoneStepContent } from './AddPhoneStepContent';
import { SmsResendCode } from './SmsResendCode';
import { dataHooks } from '../../../../stores/premiumUsers2FaEnforcement/dataHooks';
import { Steps } from '../../../../stores/premiumUsers2FaEnforcement/sms/types';
import { VerificationState } from '../../VerifyButton';

/**
 * redesigned SMS add-phone flow (wix-2025). Keeps the two-step switch:
 * the code step delegates to the shared `CodeStepCard` (same screen email/auth use);
 * the enter-phone step is a plain `EnforcementCard` (its own fields + Continue button).
 */
export const AddPhoneRedesign: React.FC = observer(() => {
  const {
    rootStore: {
      postLoginStore: {
        premiumUsers2FaEnforcementStore: { add2FAPhoneStore: store },
      },
      displayStore: { preset },
    },
  } = useContext(AppContextProvider);
  const { t } = useTranslation();
  const uiStore = new UiStore(store);
  const isCodeStep = store.activeStep === Steps.ConfirmCodeStep;

  // Start the resend cooldown when the code step appears (guarded, since this
  // component also renders the enter-phone step).
  useEffect(() => {
    if (isCodeStep) {
      store.resendSuccessfullyIndication = true;
    }
  }, [isCodeStep]);

  if (isCodeStep) {
    const subtitle = (
      <>
        {t('add_phone_wizard.enter_code.line1')}{' '}
        <ThemedText weight="bold" dir="ltr">
          {store.getSelectedInternationalPhone()}
        </ThemedText>
      </>
    );
    return (
      <CodeStepCard
        title={uiStore.title}
        subtitle={subtitle}
        onBack={uiStore.backButtonCallback}
        onVerify={store.onVerifyPhoneCode}
        verifyDisabled={
          !store.confirmCode.isValid || !store.confirmCode.isDirty
        }
        showLoader={store.verificationState !== VerificationState.Unverified}
        field={store.confirmCode}
        resend={<SmsResendCode showAsButton />}
        inputDataHook={
          dataHooks.multiFactorAuth.addPhone.modal.codeStepModal.codeInput.input
        }
      />
    );
  }

  return (
    <EnforcementCard
      priority="secondary"
      title={uiStore.title}
      subtitle={uiStore.subtitle}
      showBackButton
      backButtonCallback={uiStore.backButtonCallback}
      primaryButton={
        <ButtonWithLoader
          skin={preset.buttonDesign?.skin ?? 'standard'}
          onClick={uiStore.primaryButtonOnClick}
          disabled={store.shouldDisableSubmit}
        >
          {uiStore.primaryButtonText}
        </ButtonWithLoader>
      }
    >
      <AddPhoneStepContent compact />
    </EnforcementCard>
  );
});
